import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  ValveDescriptionValidationDto,
  ValveDescriptionRowDto,
  FileResponse,
} from 'src/app/shared/models/autogenerated-piping';

/**
 * Service to export Release Final Checks valve data to Excel files.
 * Since backend export endpoints are not yet available, this service generates
 * Excel-compatible CSV files on the client side.
 *
 * Valve Status is deliberately absent: it is exported by the backend
 * (`GET final-checks/valve-status/excel`), which produces a real workbook.
 */
@Injectable({ providedIn: 'root' })
export class ReleaseFinalChecksExportService {
  /**
   * Exports valve description validation data to Excel.
   * @param data The valve description validation DTO containing all valve description rows.
   * @returns Observable<FileResponse> with the generated Excel file.
   */
  exportValveDescriptions(data: ValveDescriptionValidationDto | undefined): Observable<FileResponse> {
    if (!data) {
      return of({ data: new Blob(), fileName: 'valve-descriptions.xlsx' } as FileResponse);
    }

    const rows: ValveDescriptionRowDto[] = [
      ...(data.modifyClassValves ?? []),
      ...(data.retireClassValves ?? []),
      ...(data.releaseAsIsClassValves ?? []),
    ];

    const csv: string = this.generateValveDescriptionCsv(rows);
    const blob: Blob = new Blob([csv], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    return of({
      data: blob,
      fileName: `valve-descriptions-${new Date().toISOString().split('T')[0]}.xlsx`,
    } as FileResponse);
  }

  /**
   * Generates CSV content for valve description data.
   * @param rows The valve description rows to export.
   * @returns CSV string with header and data rows.
   */
  private generateValveDescriptionCsv(rows: ValveDescriptionRowDto[]): string {
    const headers: string[] = [
      'Valve Tag',
      'Pipe Class ID',
      'Old Value',
      'New Value',
      'Has Mismatch',
      'Chosen Option',
    ];

    const csvRows: string[] = [this.escapeCsvRow(headers)];

    for (const row of rows) {
      csvRows.push(
        this.escapeCsvRow([
          row.valveTag ? row.valveTag.split('_')[0] : '',
          row.pipeClassId ?? '',
          row.oldValue ?? '',
          row.newValue ?? '',
          (row.descriptionMismatch && (row.oldValue ?? '') !== (row.newValue ?? '')) ? 'Yes' : 'No',
          this.getChosenOptionLabel(row.chosenOption),
        ])
      );
    }

    return csvRows.join('\n');
  }

  /**
   * Escapes and formats a row of CSV values.
   * @param values The values to format as CSV.
   * @returns Escaped CSV row string.
   */
  private escapeCsvRow(values: string[]): string {
    return values
      .map((value: string) => {
        // Escape double quotes and wrap in quotes if contains comma, quote, or newline
        const escaped: string = (value ?? '').replace(/"/g, '""');
        if (escaped.includes(',') || escaped.includes('"') || escaped.includes('\n')) {
          return `"${escaped}"`;
        }
        return escaped;
      })
      .join(',');
  }

  /** Maps a ValveDescriptionChoiceOption enum value to its display label. */
  private getChosenOptionLabel(option: number | undefined): string {
    switch (option) {
      case 0:
        return 'Keep Old Value';
      case 1:
        return 'Use New Value';
      default:
        return '';
    }
  }
}
